import React, { useState, useEffect, useCallback } from 'react'
import { FlatList, RefreshControl, Platform } from 'react-native'
import { Store as StoreIcon, ShoppingCart, Handshake, Building2, Amphora, Shirt, Tag, ChevronRight, Plus, LucideIcon, Search } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { Button, ButtonText, EmptyState, ListItem, Spinner, Text, Theme, XStack, YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantStoreSelect'>
}
interface Store {
  id: string; name: string; store_type: string; address_city: string | null; product_count: number
}

const STORE_TYPE_ICONS: Record<string, LucideIcon> = {
  self_service_flea_market: StoreIcon, full_service_flea_market: ShoppingCart,
  consignment_store: Handshake, retail_store: Building2, antique_shop: Amphora,
  vintage_store: Shirt, thrift_store: Tag,
}

export default function MerchantStoreSelectScreen({ navigation }: Props) {
  const { t, language, setLanguage } = useLanguage()
  const { user, signOut } = useAuth()
  const [stores, setStores] = useState<Store[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchStores = useCallback(async () => {
    if (!user?.id) { setLoading(false); return }
    try {
      const { data: staffData, error: staffError } = await supabase
        .from('flea_market_staff').select('flea_market_id').eq('user_id', user.id)
      if (staffError) throw staffError
      const storeIds = staffData?.map(s => s.flea_market_id) || []
      if (!storeIds.length) { setStores([]); return }

      const { data: storesData } = await supabase
        .from('flea_markets').select('id, name, store_type, address_city')
        .in('id', storeIds).not('store_type', 'is', null).order('name')

      const withCounts: Store[] = await Promise.all(
        (storesData || []).map(async store => {
          const { count } = await supabase.from('products').select('*', { count: 'exact', head: true }).eq('flea_market_id', store.id)
          return { ...store, product_count: count || 0 }
        })
      )
      setStores(withCounts)
    } catch (e) { console.error('Error fetching stores:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }, [user?.id])

  useEffect(() => { fetchStores() }, [fetchStores])

  const handleSelectStore = (store: Store) => {
    if (store.store_type === 'self_service_flea_market' || store.store_type === 'full_service_flea_market') {
      navigation.navigate('MarketAdmin', { marketId: store.id })
    } else {
      navigation.navigate('MerchantDashboard', { storeId: store.id })
    }
  }

  const renderStore = ({ item }: { item: Store }) => {
    const IconComponent = STORE_TYPE_ICONS[item.store_type] || StoreIcon
    return (
      <ListItem
        icon={<YStack w={56} h={56} br="$md" bg="rgba(184,115,51,0.1)" jc="center" ai="center"><IconComponent size={28} color="#B87333" /></YStack>}
        title={item.name}
        subtitle={`${t(item.store_type)}${item.address_city ? ` · ${item.address_city}` : ''}`}
        right={
          <XStack ai="center" gap="$sm">
            <YStack ai="center">
              <Text preset="heading" fontSize={20}>{item.product_count}</Text>
              <Text preset="caption" tone="secondary">{t('products')}</Text>
            </YStack>
            <ChevronRight size={20} color="#C8C8C2" />
          </XStack>
        }
        onPress={() => handleSelectStore(item)}
        bg="$backgroundCard" br="$lg" p="$md" mb="$sm"
        borderWidth={1} borderColor="$borderLight"
      />
    )
  }

  if (loading) return <YStack f={1} jc="center" ai="center" bg="$background"><Spinner size="large" /></YStack>

  return (
    <Theme name="brick">
      <YStack f={1} bg="$background">
        <XStack jc="space-between" ai="center" px="$lg" pt={Platform.OS === 'web' ? 16 : 52} pb="$md" bg="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderLight">
          <YStack>
            <Text preset="heading" fontSize={28}>{t('select_store')}</Text>
            <Text preset="bodySmall" tone="secondary" mt={4}>{t('select_store_to_manage')}</Text>
          </YStack>
          <XStack gap="$xs">
            <Button variant="ghost" size="sm" onPress={() => navigation.navigate('BuyerHome')}><Search size={18} color="#748C70" /></Button>
            <Button variant="ghost" size="sm" onPress={() => setLanguage(language === 'en' ? 'fi' : 'en')}><ButtonText>{language === 'en' ? 'FI' : 'EN'}</ButtonText></Button>
            <Button variant="ghost" size="sm" onPress={signOut}><ButtonText>{t('logout')}</ButtonText></Button>
          </XStack>
        </XStack>

        {stores.length === 0 ? (
          <EmptyState
            icon={<StoreIcon size={48} color="#B87333" />}
            title={t('no_stores')}
            description={t('create_first_store')}
            action={<Button variant="primary" onPress={() => navigation.navigate('CreateStore')}><ButtonText>{t('create_new_store')}</ButtonText></Button>}
          />
        ) : (
          <>
            <FlatList
              data={stores}
              renderItem={renderStore}
              keyExtractor={item => item.id}
              contentContainerStyle={{ padding: 16 }}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchStores() }} />}
            />
            <Button variant="outline" mx="$md" mb="$md" onPress={() => navigation.navigate('CreateStore')}>
              <Plus size={20} color="#B87333" />
              <ButtonText ml="$xs">{t('create_new_store')}</ButtonText>
            </Button>
          </>
        )}
      </YStack>
    </Theme>
  )
}
